<template>
  <v-card-text class="preview-step pa-0">
    <div
      :class="{
        'preview-step-content': true,
        'preview-step-content--mobile': isMobile,
        'preview-step-content--stacked': hideOffices,
      }"
    >
      <div class="preview-step-content__summary">
        <div class="preview-step-summary">
          <div class="preview-step-summary__text">
            <p class="font-weight-bold mb-0 preview-step-summary__text-title">
              {{ $t('content-creator.preview-step.summary.title') }}
            </p>
            <p
              v-if="!hideOffices"
              class="mb-0 preview-step-summary__text-description"
            >
              {{ $t('content-creator.preview-step.summary.description') }}
            </p>
          </div>
          <div class="preview-step-summary-wrapper">
            <div class="preview-step-summary__statistics">
              <div
                v-if="content.topicName"
                class="preview-step-summary__item"
              >
                <v-icon
                  icon="mdi-newspaper-variant-multiple"
                  size="small"
                />
                <span>{{ content.topicName }}</span>
              </div>
              <template
                v-for="statistic in hideOffices ? [] : statistics.slice(0, 1)"
                :key="statistic.text"
              >
                <v-tooltip
                  location="top"
                  :disabled="!statistic.tooltip"
                  open-on-focus
                >
                  <template #activator="{ props: tooltipProps }">
                    <div
                      v-bind="tooltipProps"
                      class="preview-step-summary__item"
                      tabindex="0"
                    >
                      <v-icon
                        :icon="statistic.icon"
                        size="small"
                      />
                      <span>{{ statistic.text }}</span>
                    </div>
                  </template>
                  {{ statistic.tooltip }}
                </v-tooltip>
              </template>
            </div>
            <div class="preview-step-summary__statistics">
              <template
                v-for="statistic in hideOffices ? statistics : statistics.slice(1)"
                :key="statistic.text"
              >
                <v-tooltip
                  location="top"
                  :disabled="!statistic.tooltip"
                  open-on-focus
                >
                  <template #activator="{ props: tooltipProps }">
                    <div
                      v-bind="tooltipProps"
                      class="preview-step-summary__item"
                      tabindex="0"
                    >
                      <v-icon
                        :icon="statistic.icon"
                        size="small"
                      />
                      <span>{{ statistic.text }}</span>
                    </div>
                  </template>
                  {{ statistic.tooltip }}
                </v-tooltip>
              </template>
            </div>
          </div>
        </div>
        <div
          class="preview-step-offices"
          v-if="!isMobile && !hideOffices"
        >
          <p class="mb-4 font-weight-bold preview-step-offices__title">
            {{ tableTitle || $t('content-creator.preview-step.preview-offices.title') }}
          </p>
          <component
            :is="previewTableComponent"
            fixed-header
            :headers="headers"
            :items="offices"
            v-bind="previewTableBind"
            height="45vh"
            hide-default-footer
          >
            <template
              v-if="itemsLength !== undefined && itemsLength > itemsPerPage"
              #bottom
            >
              <v-pagination
                v-model="internalPage"
                :total-visible="6"
                :length="Math.ceil(itemsLength / itemsPerPage)"
                rounded
              />
            </template>
            <template #[`item.name`]="{ item }">
              <div class="d-flex ga-2">
                <v-tooltip
                  v-if="item?.extras?.isPersonalizedModel && !item?.extras?.ambassadorImage"
                  location="top"
                >
                  <template #activator="{ props: tooltipPropsNoImage }">
                    <v-icon
                      v-bind="tooltipPropsNoImage"
                      icon="mdi-information-outline"
                    />
                  </template>
                  {{ $t('content-creator.preview-step.personalized-model.tooltip') }}
                </v-tooltip>
                <v-tooltip location="top">
                  <template #activator="{ props: tooltipProps }">
                    <span v-bind="tooltipProps">{{ item.name }}</span>
                  </template>
                  {{ item.name }}
                </v-tooltip>
              </div>
            </template>
            <template #[`item.socialMediaCount`]="{ item }">
              <div class="d-flex flex-column gap-1">
                <span
                  v-if="
                    !getSocialMediaAccounts(item) ||
                    (Array.isArray(getSocialMediaAccounts(item)) &&
                      getSocialMediaAccounts(item).length === 0)
                  "
                  class="text-red"
                >
                  {{ isNewsletterMode ? '0' : $t('content-creator.not-connected') }}
                </span>
                <div
                  v-else-if="Array.isArray(getSocialMediaAccounts(item))"
                  class="d-flex flex-column ga-1 py-2"
                >
                  <div
                    v-for="(account, index) in getSocialMediaAccounts(item)"
                    :key="`${account.platform}-${index}`"
                    class="d-flex align-center ga-2"
                  >
                    <v-avatar
                      v-if="getIconForPlatform(account.platform)"
                      size="20"
                      tile
                    >
                      <r-img :src="getIconForPlatform(account.platform)" />
                    </v-avatar>
                    <span
                      :class="{ 'text-blue': account.username, 'text-red': !account.username }"
                      >{{
                        account.username ||
                        account.statusLabel ||
                        $t('content-creator.not-connected')
                      }}</span
                    >
                  </div>
                </div>
                <span
                  v-else
                  :class="isNewsletterMode ? '' : 'text-blue'"
                  class="text-blue"
                >
                  {{ getSocialMediaAccounts(item) }}
                </span>
              </div>
            </template>
            <template #[`item.postUrl`]="{ item }">
              <div class="d-flex flex-column gap-1 py-2">
                <div
                  v-if="Array.isArray(item.postUrl) && item.postUrl.length > 0"
                  class="d-flex flex-column ga-1"
                >
                  <v-tooltip
                    v-for="(postUrl, index) in item.postUrl"
                    :key="`${postUrl.platform}-${index}`"
                    location="top"
                  >
                    <template #activator="{ props }">
                      <div class="d-flex align-center ga-2">
                        <a
                          v-bind="props"
                          :href="postUrl.url"
                          target="_blank"
                          class="text-blue text-truncate"
                        >
                          {{ postUrl.url }}
                        </a>
                      </div>
                    </template>
                    <span>{{ postUrl.url }}</span>
                  </v-tooltip>
                </div>
                <v-tooltip
                  v-else-if="item.postUrl && !Array.isArray(item.postUrl)"
                  location="top"
                >
                  <template #activator="{ props }">
                    <a
                      v-bind="props"
                      :href="item.postUrl"
                      target="_blank"
                      class="text-truncate d-block"
                    >
                      {{ item.postUrl }}
                    </a>
                  </template>
                  <span>{{ item.postUrl }}</span>
                </v-tooltip>
              </div>
            </template>
            <template #[`item.targetLink`]="{ item }">
              <v-tooltip
                v-if="item.targetLink"
                location="top center"
              >
                <template #activator="{ props }">
                  <a
                    v-bind="props"
                    :href="item.targetLink"
                    class="target-link text-truncate w-full d-block"
                    target="_blank"
                  >
                    {{ item.targetLink }}
                  </a>
                </template>
                <span>{{ item.targetLink }}</span>
              </v-tooltip>
            </template>
            <template #[`item.status`]="{ item }">
              <div class="d-flex flex-column ga-1 py-2">
                <div
                  v-for="(account, index) in getSocialMediaAccounts(item)"
                  :key="`${account.platform}-${index}`"
                  class="d-flex align-center ga-2"
                >
                  <v-avatar
                    v-if="getIconForPlatform(account.platform)"
                    size="20"
                    tile
                  >
                    <r-img :src="getIconForPlatform(account.platform)" />
                  </v-avatar>

                  <v-tooltip location="top">
                    <template #activator="{ props }">
                      <span
                        v-bind="props"
                        :class="getPostStatusClass(account.postStatus)"
                      >
                        {{ getPostStatusLabel(account.postStatus) }}
                      </span>
                    </template>

                    <span>{{ getPostStatusLabel(account.postStatus) }}</span>
                  </v-tooltip>
                </div>
              </div>
            </template>
            <template
              v-if="newsletterHistoryRecipientMode"
              #[`item.recipientEmail`]="{ item }"
            >
              <v-tooltip location="top">
                <template #activator="{ props }">
                  <span
                    v-bind="props"
                    class="text-body-2 text-truncate d-block"
                    >{{ item.recipientEmail }}</span
                  >
                </template>
                <span>{{ item.recipientEmail }}</span>
              </v-tooltip>
            </template>
            <template
              v-if="newsletterHistoryRecipientMode"
              #[`item.deliveryStatus`]="{ item }"
            >
              <div class="d-flex justify-items-center align-center ga-1">
                <v-icon
                  v-if="item.deliveryStatus === 'sent'"
                  color="success"
                  size="small"
                  icon="mdi-check-circle"
                />
                <v-tooltip
                  v-else-if="item.deliveryStatus === 'failed'"
                  location="top"
                >
                  <template #activator="{ props: tip }">
                    <span v-bind="tip">
                      <v-icon
                        color="error"
                        size="small"
                        icon="mdi-alert-circle"
                      />
                    </span>
                  </template>
                  <span>{{
                    item.deliveryError ||
                    $t('content-creator.newsletter-details.delivery-error-unknown')
                  }}</span>
                </v-tooltip>
                <v-icon
                  v-else
                  color="grey"
                  size="small"
                  icon="mdi-clock-outline"
                />
                <span class="text-caption text-medium-emphasis">
                  {{ deliveryStatusLabel(item.deliveryStatus) }}
                </span>
              </div>
            </template>
            <template
              v-if="showNewsletterDeliveryColumn && !newsletterHistoryRecipientMode"
              #[`item.delivery`]="{ item }"
            >
              <div class="d-flex align-center ga-1">
                <template v-if="(item.failedCount || 0) > 0">
                  <span class="text-error text-body-2">
                    {{
                      $t('content-creator.newsletter-details.delivery-failed-count', {
                        n: item.failedCount,
                      })
                    }}
                  </span>
                  <v-tooltip
                    v-if="item.failedDeliveryError"
                    location="top"
                  >
                    <template #activator="{ props: tip }">
                      <v-icon
                        v-bind="tip"
                        size="small"
                        icon="mdi-information-outline"
                      />
                    </template>
                    <span>{{ item.failedDeliveryError }}</span>
                  </v-tooltip>
                </template>
                <v-icon
                  v-else
                  color="success"
                  size="small"
                  icon="mdi-check-circle"
                />
              </div>
            </template>
          </component>
        </div>
      </div>
      <div class="preview-step__preview">
        <p
          v-if="!hideOffices"
          class="text-primary font-weight-bold text-title-large mb-4"
        >
          {{ previewTitle || $t('content-creator.preview-step.preview.title') }}
        </p>
        <slot name="preview">
          <div class="preview-step__preview-post">
            <template v-if="content.textKind !== PostType.newsletter">
              <p
                v-for="(text, index) in formattedText"
                :key="`${text}-${index}`"
              >
                {{ text }}
              </p>
            </template>

            <h3
              class="subject-line"
              v-if="content.textKind === PostType.newsletter"
              v-html="content.subject"
            />
            <template v-if="content?.image">
              <slot
                name="media"
                :src="content.image"
              >
                <v-img
                  v-if="!isVideoFile(content.image)"
                  :src="content.image"
                  :alt="content.text"
                />

                <p v-else>
                  <video
                    class="w-full"
                    :src="content.image"
                    controls
                  />
                </p>
              </slot>
              <br />
            </template>
            <template v-if="content.textKind === PostType.newsletter">
              <p
                v-for="(text, index) in htmlReplace(content.text)?.split('\n').filter(Boolean)"
                :key="`${text}-${index}`"
              >
                <span v-html="text" />
              </p>
              <span v-html="signatureHtml" />
            </template>
          </div>
        </slot>
      </div>
    </div>
    <div
      v-if="$slots.actions"
      class="preview-step__slot-actions"
    >
      <slot name="actions" />
    </div>
    <v-card-actions
      v-else-if="showBackButton || !hideSubmitButton"
      class="preview-step__actions"
    >
      <v-btn
        v-if="showBackButton"
        @click="emit('back')"
        variant="text"
      >
        {{ $t('labels.back') }}
      </v-btn>
      <v-spacer />
      <v-btn
        v-if="!hideSubmitButton"
        @click="emit('submit')"
        :prepend-icon="submitIcon"
        :loading="isPosting"
        :disabled="isPosting || disabled"
      >
        {{ submitText }}
      </v-btn>
    </v-card-actions>
  </v-card-text>
</template>
<script lang="ts" setup>
import { computed, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import { VDataTable, VDataTableServer } from 'vuetify/components'

import { formatDate, getTime } from '@/lib/dateTime'
import { htmlReplace } from '@/lib/truncate'
import { isVideoFile } from '@/modules/adMaterials/fileTypes'
import { useCompanyQuery } from '@/modules/contentCreator/creator/composables/useCompanyQuery'
import { PostStatus } from '@/modules/contentCreator/creator/enums/PostStatus'
import { PostType } from '@/modules/contentCreator/creator/enums/PostType'

import { KindTypeIcon, TypeToKind } from '../enums/KindType'

const { t } = useI18n()
const { smAndDown: isMobile } = useDisplay()

type Content = {
  topicName?: string
  image?: string
  text: string
  subject: string
  date?: string
  textKind?: string
  publication?: string
}

type Props = {
  // TODO: Change to correct type
  offices: any[]
  showPostColumn: boolean
  isScheduledPost?: boolean
  content: Content
  submitText: string
  submitIcon?: string
  showBackButton?: boolean
  isPosting?: boolean
  disabled?: boolean
  hideSubmitButton?: boolean
  /** Single-column post details: summary and preview, no offices table. */
  hideOffices?: boolean
  previewTitle?: string
  customStatistics?: Array<{ icon: string; text: string }>
  tableTitle?: string
  secondColumnTitle?: string
  isNewsletterMode?: boolean
  isHistory?: boolean
  page?: number
  itemsLength?: number
  isLoadingOffices?: boolean
  itemsPerPage?: number
  /** Single column (email); header title uses secondColumnTitle */
  recipientOnlyTable?: boolean
  /** Per-recipient rows (name, email, delivery) for single-tenant newsletter history */
  newsletterHistoryRecipientMode?: boolean
  /** Extra column: delivery health per office (multi-tenant) */
  showNewsletterDeliveryColumn?: boolean
}

interface AuthService {
  user?: {
    companyId: string
  }
}

type Emits = {
  (e: 'back'): void
  (e: 'submit'): void
  (e: 'update:page', page: number): void
  (e: 'update:itemsPerPage', value: number): void
}

const emit = defineEmits<Emits>()
const auth = inject<AuthService>('auth')
const { company } = useCompanyQuery(auth)

const internalPage = computed({
  get: () => props.page,
  set: (value) => emit('update:page', value),
})

const signatureHtml = computed(() => {
  return {
    ['all']: t('content-creator.newsletter-details.signature.all'),
    ['head']:
      company.value?.ambassador?.signature &&
      company.value.ambassador.signature.customTemplateSignature
        ? company.value.ambassador.signature.html || company.value.ambassador.signature.text
        : '',
    ['individual']: t('content-creator.newsletter-details.signature.individual'),
  }[props.content?.publication || 'head']
})

const props = withDefaults(defineProps<Props>(), {
  // TODO: Remove default value
  showPostColumn: false,
  showBackButton: false,
  isPosting: false,
  disabled: false,
  hideSubmitButton: false,
  hideOffices: false,
  isHistory: false,
  page: 1,
  itemsPerPage: 10,
  recipientOnlyTable: false,
  newsletterHistoryRecipientMode: false,
  showNewsletterDeliveryColumn: false,
})

const previewTableComponent = computed(() =>
  props.itemsLength !== undefined ? VDataTableServer : VDataTable
)

const previewTableBind = computed(() => {
  if (props.itemsLength !== undefined) {
    return {
      itemsLength: props.itemsLength,
      loading: props.isLoadingOffices,
      itemsPerPage: props.itemsPerPage,
      itemsPerPageOptions: [10, 25, 50, 100],
      page: props.page,
      'onUpdate:page': (p: number) => emit('update:page', p),
      'onUpdate:itemsPerPage': (n: number) => emit('update:itemsPerPage', n),
    }
  }
  return {
    hideDefaultFooter: true,
    loading: props.isLoadingOffices,
  }
})

type Statistic = {
  icon: string
  text: string
  tooltip?: string
}

const socialMediaAccountSummary = computed(() => {
  return props.offices.reduce(
    (summary, office) => {
      const accounts = getSocialMediaAccounts(office)
      if (Array.isArray(accounts)) {
        summary.total += accounts.length
        summary.connected += accounts.filter((account) => account?.username).length
      } else if (accounts) {
        summary.total += 1
        summary.connected += 1
      }
      return summary
    },
    { total: 0, connected: 0 }
  )
})

const socialMediaAccountsTooltip = computed(() => {
  if (props.isHistory || props.isNewsletterMode) return undefined

  return t('content-creator.preview-step.accounts-tooltip', {
    connected: socialMediaAccountSummary.value.connected,
    notConnected: socialMediaAccountSummary.value.total - socialMediaAccountSummary.value.connected,
  })
})

const socialMediaAccounts = computed(() => {
  return socialMediaAccountSummary.value.total
})

const statistics = computed<Statistic[]>(() => {
  if (props.customStatistics) {
    return props.customStatistics
  }

  return [
    {
      icon: 'mdi-calendar-month-outline',
      text: props.content?.date
        ? formatDate(new Date(props.content.date), 'dateLong')
        : formatDate(new Date(), 'dateLong'),
    },
    {
      icon: 'mdi-calendar-clock',
      text: props.content?.date ? getTime(new Date(props.content.date)) : t('labels.now'),
    },
    {
      icon: 'mdi-office-building-outline',
      text: t('content-creator.preview-step.offices', { n: props.offices.length }),
    },
    {
      icon: 'mdi-badge-account-horizontal-outline',
      text: t('content-creator.preview-step.accounts', { n: socialMediaAccounts.value }),
      tooltip: socialMediaAccountsTooltip.value,
    },
  ]
})

const columnWidth = computed(() => {
  if (props.recipientOnlyTable) return '100%'
  if (props.newsletterHistoryRecipientMode) return '33.33%'
  if (props.showNewsletterDeliveryColumn && props.isNewsletterMode) return '25%'
  return props.showPostColumn ? '25%' : '33.33%'
})

function deliveryStatusLabel(status: string | undefined) {
  if (status === 'sent') return t('content-creator.newsletter-details.delivery-status.sent')
  if (status === 'failed') return t('content-creator.newsletter-details.delivery-status.failed')
  return t('content-creator.newsletter-details.delivery-status.pending')
}

const headers = computed<InstanceType<typeof VDataTable>['$props']['headers']>(() => {
  if (props.newsletterHistoryRecipientMode) {
    return [
      {
        title: t('content-creator.newsletter-details.table.recipient'),
        key: 'name',
        width: columnWidth.value,
      },
      {
        title: t('content-creator.newsletter-details.table.email'),
        key: 'recipientEmail',
        width: columnWidth.value,
      },
      !props.isScheduledPost && {
        title: t('content-creator.newsletter-details.table.delivery-status'),
        key: 'deliveryStatus',
        width: columnWidth.value,
      },
    ].filter(Boolean)
  }

  if (props.recipientOnlyTable) {
    return [
      {
        title:
          props.secondColumnTitle ||
          t('content-creator.preview-step.preview-offices.table.social-media-accounts'),
        key: 'socialMediaCount',
        width: columnWidth.value,
      },
    ]
  }

  const row = [
    {
      title: t('content-creator.preview-step.preview-offices.table.offices'),
      key: 'name',
      width: columnWidth.value,
    },
    {
      title:
        props.secondColumnTitle ||
        t('content-creator.preview-step.preview-offices.table.social-media-accounts'),
      key: 'socialMediaCount',
      width: columnWidth.value,
    },
    ...(props.showPostColumn
      ? [
          {
            title: t('content-creator.preview-step.preview-offices.table.post-url'),
            key: 'postUrl',
            width: columnWidth.value,
          },
        ]
      : []),
    {
      title: t('content-creator.preview-step.preview-offices.table.target-link'),
      key: 'targetLink',
      width: columnWidth.value,
    },
    ...(props.isHistory || props.isScheduledPost
      ? [
          {
            title: t('content-creator.preview-step.preview-offices.table.status'),
            key: 'status',
            width: columnWidth.value,
          },
        ]
      : []),
  ]

  if (props.showNewsletterDeliveryColumn && props.isNewsletterMode) {
    return [
      ...row,
      !props.isScheduledPost && {
        title: t('content-creator.newsletter-details.table.delivery-status'),
        key: 'delivery',
        width: columnWidth.value,
      },
    ].filter(Boolean)
  }

  return row
})

const formattedText = computed(() => {
  if (!props.content.text || props.content.text.length === 0) return []
  return props.content.text.split('\n').filter(Boolean)
})

const getIconForPlatform = (platform: string): string | null => {
  const kind = TypeToKind[platform]
  return kind ? KindTypeIcon[kind] : null
}

const getSocialMediaAccounts = (item) => item?.socialMediaCount || item?.socialMedia

const getPostStatusClass = (status: string) => {
  switch (status) {
    case PostStatus.SUCCESSFUL:
      return 'text-primary'
    case PostStatus.SCHEDULED:
      return 'text-warning'
    case PostStatus.FAILED:
      return 'text-error'
    case PostStatus.NOT_CONNECTED:
      return 'text-error'
    default:
      return ''
  }
}

const getPostStatusLabel = (status: string) => {
  switch (status) {
    case PostStatus.SUCCESSFUL:
      return t('content-creator.preview-step.preview-offices.table.post-status.successful')
    case PostStatus.SCHEDULED:
      return t('content-creator.preview-step.preview-offices.table.post-status.scheduled')
    case PostStatus.FAILED:
      return t('content-creator.preview-step.preview-offices.table.post-status.failed')
    case PostStatus.NOT_CONNECTED:
      return t('content-creator.preview-step.preview-offices.table.post-status.not-connected')
    default:
      return status
  }
}
</script>
<style scoped>
.preview-step {
  display: flex;
  flex-direction: column;
}
.preview-step-content {
  flex-grow: 1;
  display: grid;
  grid-template-columns: auto 340px;
  gap: 32px;
  max-height: 70vh;
  padding-bottom: 24px;
  overflow: hidden;
}
.preview-step-content__summary {
  max-height: 70vh;
  overflow-y: auto;
}
.preview-step__preview {
  max-height: 70vh;
  overflow-y: auto;
}
.preview-step-content--mobile,
.preview-step-content--stacked {
  display: flex;
  flex-direction: column;
  max-height: none;
  overflow: visible;
}
.preview-step-content--stacked .preview-step-content__summary,
.preview-step-content--stacked .preview-step__preview {
  max-height: none;
  overflow: visible;
}
.preview-step__slot-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px 0 4px;
}
.preview-step__actions {
  display: flex;
  justify-content: end;
  border-top: 1px solid #e0e0e0;
}
.preview-step-summary-wrapper {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.preview-step-summary {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.preview-step-summary__item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
}
.preview-step-summary__statistics {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}
.preview-step-offices {
  padding: 16px 0;
}
.preview-step-offices__title {
  font-size: 1rem;
}

.preview-step-summary__text {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.preview-step-summary__text-title {
  font-size: 1.25rem;
  color: rgb(var(--v-theme-primary));
}

.preview-step-summary__text-description {
  font-size: 1rem;
}

.preview-step-offices :deep(.v-table__wrapper) {
  overflow-y: auto;
}
.platform-dropdown {
  max-width: 160px;
}

:deep(.v-table.v-table--fixed-header > .v-table__wrapper > table > thead > tr > th) {
  background-color: #eef4f4;
  color: rgb(var(--v-theme-primary));
}
a.target-link:not(.nav-item):not(.v-alert__content a):not(.v-btn) {
  color: rgb(var(--v-theme-orange)) !important;
}

:deep(.v-data-table) {
  table {
    table-layout: fixed;
    width: 100%;
  }

  th,
  td {
    width: v-bind(columnWidth);
    min-width: v-bind(columnWidth);
    max-width: v-bind(columnWidth);
  }
}

/* Ensure text truncation works */
:deep(.v-data-table td) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.subject-line {
  margin: 0 0 20px 0;
  font-weight: bold;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 16px;
  color: rgb(var(--v-theme-primary));
}
.break-row {
  flex-basis: 100%;
  height: 0;
}

.offices-title {
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0;
  color: rgb(var(--v-theme-primary));
}
</style>
